import { useState, useEffect, useCallback } from 'react';
import {
  Composite,
  CompositeListResponse,
  CompositionUpdateBody,
  Product,
  ProductUpdate,
  FamilyLookupItem, // NEW: Import for type safety
  SubFamilyLookupItem, // NEW: Import for type safety
  BarcodeType1LookupItem, // NEW: Import for type safety
  BarcodeType2LookupItem, // NEW: Import for type safety
} from '../types/costwand';
import { apiClient, ApiError } from '../api/apiClient.tsx';

const PAGE_SIZE = 10;

interface UseCompositesResult {
  composites: Composite[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchComposites: (page: number) => Promise<void>;
  handlePageChange: (page: number) => void;
  updateCompositeDetails: ( // RENAMED: from updateCompositePricingDetails
    id: number,
    data: {
      description: string; // NEW
      family_id: number; // NEW
      subfamily_id: number | null; // NEW
      barcode_1_id: number | null; // NEW
      barcode_2_id: number | null; // NEW
      tax_profile_id: number;
      markup_perc_wholesale: number;
      markup_perc_retail: number;
    }
  ) => Promise<boolean>;
  updateCompositeComposition: (id: number, composition: CompositionUpdateBody) => Promise<boolean>;
  deleteComposite: (id: number) => Promise<boolean>;
}

export const useComposites = (): UseCompositesResult => {
  const [composites, setComposites] = useState<Composite[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchComposites = useCallback(async (page: number) => {
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<CompositeListResponse>(
        `/composites?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true }
      );
      setComposites(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch Composites: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching Composites.');
      }
      setComposites([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchComposites(currentPage);
  }, [currentPage, fetchComposites]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // RENAMED: from updateCompositePricingDetails to updateCompositeDetails
  // NEW: Updated signature to accept all pricing details and new fields
  const updateCompositeDetails = async (
    id: number,
    data: {
      description: string; // NEW
      family_id: number; // NEW
      subfamily_id: number | null; // NEW
      barcode_1_id: number | null; // NEW
      barcode_2_id: number | null; // NEW
      tax_profile_id: number;
      markup_perc_wholesale: number;
      markup_perc_retail: number;
      retail_price: number;
    }
  ): Promise<boolean> => {
    setError(null);
    try {
      const payload: Partial<ProductUpdate> = {
        description: data.description, // NEW: Include description
        family_id: data.family_id, // NEW: Include family_id
        subfamily_id: data.subfamily_id, // NEW: Include subfamily_id
        barcode_1_id: data.barcode_1_id, // NEW: Include barcode_1_id
        barcode_2_id: data.barcode_2_id, // NEW: Include barcode_2_id
        tax_profile_id: data.tax_profile_id,
        markup_perc_wholesale: data.markup_perc_wholesale,
        markup_perc_retail: data.markup_perc_retail,
        retail_price: data.retail_price,
        updated_by: 'tester',
      };
      await apiClient<Product>(`/products/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      await fetchComposites(currentPage); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      return false;
    }
  };

  const updateCompositeComposition = async (id: number, composition: CompositionUpdateBody): Promise<boolean> => {
    setError(null);
    try {
      const payload: CompositionUpdateBody = {
        ...composition
      };
      await apiClient<Composite>(`/composites/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      await fetchComposites(currentPage); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      return false;
    }
  };

  const deleteComposite = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Composite ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/composites/${id}`, { method: 'DELETE' });
      const pageToFetch = composites.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchComposites(pageToFetch); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      return false;
    }
  };

  return {
    composites,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchComposites,
    handlePageChange,
    updateCompositeDetails, // NEW: Export renamed function
    updateCompositeComposition,
    deleteComposite,
  };
};